import { Badge, Button, Checkbox, SignalDot } from '@indipia/ui-core'
import { formatMD, signalPresentation, type SignalItem } from '@indipia/shared'
import type { OrderVendorRow, ScheduleTaskOrderItem } from '../../api/orderApi'
import {
  ASSIGN_METHOD, canRelease, formatAssignSummary, ORDER_MESSAGE, orderStatusBadge, isVendorLocked, summarizeAssign,
  type MaterialTaskRowModel, type OrderDraft,
} from '../../constants/materialOrder.logic'
import { SIGNAL_COLOR_LABEL, signalCtx } from '../../constants/signalView'
import { VendorPicker } from './VendorPicker'

// C-3 팝업 좌측 — 공종(task) 1줄 목록. 직접발주 아님은 빗금 점·'직접발주 아님'만 보이고 거래처·체크·버튼이 없다.

/** 신호 조회 상태 — 오류·없음은 폴백 없이 오류 표시 */
export type TaskSignalView =
  | { state: 'loading' }
  | { state: 'error' }
  | { state: 'ok'; item: SignalItem }

interface Props {
  rows: MaterialTaskRowModel[]
  selectedTaskId: number | null
  selectedRowId: number | null
  checked: ReadonlySet<number>
  /** 일괄 체크가 가능한 원장 행 id */
  batchableIds: ReadonlySet<number>
  signalOf: (taskId: number) => TaskSignalView
  /** 행의 입력 초안(작성 상태 배지용) — 선택 행은 입력 중인 값, 나머지는 서버 값 */
  draftOf: (row: OrderVendorRow) => OrderDraft
  onSelect: (taskId: number, orderVendorId: number | null) => void
  onToggleCheck: (orderVendorId: number) => void
  onOpenEvidence: (taskId: number, row: OrderVendorRow) => void
  onPickVendor: (task: ScheduleTaskOrderItem, row: OrderVendorRow | null, vendorId: number) => void
  onReleaseVendor: (row: OrderVendorRow) => void
}

function periodText(m: MaterialTaskRowModel): string {
  const a = formatMD(m.startDate)
  const b = formatMD(m.endDate)
  return a && b ? ORDER_MESSAGE.popup.periodRange(a, b) : ''
}

function SignalLine({ view, m }: { view: TaskSignalView; m: MaterialTaskRowModel }) {
  if (view.state === 'loading') return <span className="text-caption text-text-muted" aria-hidden="true" />
  if (view.state === 'error') {
    return (
      <span className="flex items-center gap-1 text-caption text-error" role="alert">
        <SignalDot color="RED" label={ORDER_MESSAGE.popup.signalErrorBadge} />
        <span>{ORDER_MESSAGE.signalError}</span>
      </span>
    )
  }
  const ctx = signalCtx(view.item, {
    startDate: m.startDate,
    endDate: m.endDate,
    vendorName: m.vendorRows[0]?.vendorName ?? m.task.vendorName,
  })
  const pres = signalPresentation(view.item.reasonCode, ctx)
  if (!pres) {
    return <span className="text-caption text-error" role="alert">{ORDER_MESSAGE.signalUnknown}</span>
  }
  return (
    <span className="flex w-full min-w-0 items-center gap-1 text-caption">
      <SignalDot color={view.item.colorCode} label={ORDER_MESSAGE.popup.signalBadge(SIGNAL_COLOR_LABEL[view.item.colorCode])} />
      <b className="shrink-0 text-text-primary">{pres.name}</b>
      <span className="min-w-0 truncate text-text-muted">{pres.line}</span>
    </span>
  )
}

function DateLine({ m }: { m: MaterialTaskRowModel }) {
  const o = formatMD(m.orderDate)
  const d = formatMD(m.deliveryDate)
  if (!o || !d) return <span className="text-caption text-text-muted">{ORDER_MESSAGE.datesMissing}</span>
  return (
    <span className="flex items-center gap-1 text-caption text-text-muted">
      <span>{`발주 ${o} → 입고 ${d}`}</span>
      {m.deliveryConfirmed && <Badge status="success">{ORDER_MESSAGE.deliveryConfirmedBadge}</Badge>}
    </span>
  )
}

export function MaterialTaskList(p: Props) {
  return (
    <>
      <ul className="flex flex-col gap-2" aria-label={ORDER_MESSAGE.popup.listTitle}>
        {p.rows.map(m => {
          const taskSelected = p.selectedTaskId === m.taskId
          const lines: Array<OrderVendorRow | null> = m.vendorRows.length > 0 ? m.vendorRows : [null]
          const firstRow = m.vendorRows[0] ?? null
          return (
            <li
              key={m.taskId}
              data-task-id={m.taskId}
              className={`flex cursor-pointer flex-col gap-2 rounded-md border p-3 ${taskSelected ? 'border-point bg-point-subtle' : 'border-border'}`}
              aria-current={taskSelected ? 'true' : undefined}
              // 카드 아무 곳(날짜 줄·배지·빈 곳)을 눌러도 이 공종을 오른쪽에 연다 — 키보드는 머리 줄 버튼으로 같은 동작
              onClick={() => p.onSelect(m.taskId, firstRow ? firstRow.orderVendorId : null)}
            >
              {/* 머리 줄(공종명 · 기간 + 상태 한 줄) — btn 은 높이가 고정이고 줄바꿈을 막아 두 줄 내용이 넘치므로 높이 · 여백 · 줄바꿈을 풀어 쓴다 */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full min-w-0 !h-auto !p-0 !justify-start text-left !whitespace-normal hover:!bg-transparent"
                aria-label={`${m.workTypeName} ${periodText(m)}`}
                onClick={e => { e.stopPropagation(); p.onSelect(m.taskId, firstRow ? firstRow.orderVendorId : null) }}
              >
                <span className="flex w-full min-w-0 flex-col items-start gap-1">
                  <span className="flex w-full min-w-0 items-baseline gap-2">
                    {!m.directOrder && <SignalDot color="NONE" />}
                    <b className="truncate text-body-sm text-text-primary">{m.workTypeName}</b>
                    <span className="shrink-0 text-caption text-text-muted">{periodText(m)}</span>
                  </span>
                  {m.directOrder
                    ? <SignalLine view={p.signalOf(m.taskId)} m={m} />
                    : <span className="text-caption text-text-muted">{ORDER_MESSAGE.notDirect}</span>}
                </span>
              </Button>

              {m.directOrder && (
                <div className="flex min-w-0 flex-col gap-2">
                  <DateLine m={m} />
                  {/* 거래처 줄들은 열(체크 · 거래처 선택 · 상태 배지 · 배정 배지)을 함께 쓴다 —
                      배지 글자 길이(작성완료·작성중 등)가 달라도 거래처 선택 칸 폭이 줄마다 같다 */}
                  <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-2">
                  {lines.map(row => {
                    const rowSelected = taskSelected && row != null && p.selectedRowId === row.orderVendorId
                    const locked = row != null && isVendorLocked(row.orderStatusCode)
                    const statusBadge = row ? orderStatusBadge(row, p.draftOf(row)) : null
                    return (
                      <div
                        key={row ? row.orderVendorId : 'none'}
                        className="col-span-full grid grid-cols-subgrid items-center"
                        aria-current={rowSelected ? 'true' : undefined}
                        // 거래처 줄은 카드 대표 행이 아니라 이 행을 연다(거래처가 여러 곳일 때)
                        onClick={e => { e.stopPropagation(); p.onSelect(m.taskId, row ? row.orderVendorId : null) }}
                      >
                        {row ? (
                          // 일괄 발송 체크는 선택 행을 바꾸지 않는다
                          <span className="inline-flex" onClick={e => e.stopPropagation()}>
                          <Checkbox
                            bare
                            aria-label={ORDER_MESSAGE.popup.batchCheck(`${m.workTypeName} ${row.vendorName}`)}
                            disabled={!p.batchableIds.has(row.orderVendorId)}
                            checked={p.checked.has(row.orderVendorId)}
                            onChange={() => p.onToggleCheck(row.orderVendorId)}
                          />
                          </span>
                        ) : <span aria-hidden="true" />}
                        <div className="min-w-0" onFocusCapture={() => p.onSelect(m.taskId, row ? row.orderVendorId : null)}>
                          <VendorPicker
                            row={row}
                            taskId={m.taskId}
                            disabled={locked}
                            lockedReason={locked ? ORDER_MESSAGE.vendorLockedReason : undefined}
                            onPick={vendorId => p.onPickVendor(m.task, row, vendorId)}
                            onRelease={row && canRelease(row) ? () => p.onReleaseVendor(row) : undefined}
                          />
                        </div>
                        <span className="flex">
                          {statusBadge && <Badge status={statusBadge.status}>{statusBadge.label}</Badge>}
                          {row == null && <Badge status="mute">{ORDER_MESSAGE.unassigned}</Badge>}
                        </span>
                        <span className="flex">
                        {row?.assignMethodCode === ASSIGN_METHOD.AUTO && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            aria-label={ORDER_MESSAGE.evidenceOpen}
                            onClick={() => p.onOpenEvidence(m.taskId, row)}
                          >
                            <Badge status="info">{ORDER_MESSAGE.assignAutoInfo}</Badge>
                          </Button>
                        )}
                        {row?.assignMethodCode === ASSIGN_METHOD.MANUAL && <Badge status="mute">{ORDER_MESSAGE.assignManual}</Badge>}
                        </span>
                      </div>
                    )
                  })}
                  </div>
                </div>
              )}
            </li>
          )
        })}
      </ul>
      <p className="mt-2 text-caption text-text-muted">{formatAssignSummary(summarizeAssign(p.rows))}</p>
    </>
  )
}
